import { Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import moment from "moment";
import * as React from "react";
import logo from "../../directitgroupcourier.jpg";
import httpRequest from "../../HttpService";
import { getLocationString, getStatusString } from "../../newOrder/components/helperFunctions/index.js";
import "../orderDetail.css";
import TagsPanel from "./TagsPanel";

const tzMap = {
    "Pacific Standard Time": "America/Los_Angeles",
    "Mountain Standard Time": "America/Edmonton",
    "Central Standard Time": "America/Chicago",
    "Eastern Standard Time": "America/New_York",
    "Atlantic Standard Time": "America/Halifax",
    "Newfoundland Standard Time": "America/St_Johns",
};

const Field = ({ label, value }) => (
    <div style={{ marginBottom: 12 }}>
        <div
            style={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#1a1a1a",
                opacity: 0.4,
                marginBottom: 2,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 12,
                color: value ? "#1a1a1a" : "#bbb",
                fontWeight: value ? 500 : 400,
                opacity: value ? 0.85 : 0.35,
            }}
        >
            {value || "—"}
        </div>
    </div>
);

const SectionHeading = ({ children, noBorder = false }) => (
    <div
        style={{
            fontSize: 9,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "#555",
            borderBottom: noBorder ? "none" : "1px solid #ddd",
            paddingBottom: noBorder ? 0 : 5,
            marginBottom: 12,
        }}
    >
        {children}
    </div>
);

const StatusCell = ({ label, value, color, mono, colSpan }) => (
    <div style={{ gridColumn: `span ${colSpan}`, padding: "8px 10px", borderRight: "1px solid #e8e8e8" }}>
        <div
            style={{
                fontSize: 8,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.7px",
                color: "#1a1a1a",
                opacity: 0.4,
                marginBottom: 3,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 11,
                fontWeight: color ? 700 : 600,
                color: color || "#1a1a1a",
                opacity: 0.85,
                fontFamily: mono ? "monospace" : "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {value || "—"}
        </div>
    </div>
);

const ReceiptTimeline = ({ steps }) => {
    if (!steps || !steps.length) return null;
    return (
        <Stepper alternativeLabel style={{ padding: "4px 0", background: "transparent", width: "100%" }}>
            {steps.map((step) => (
                <Step key={step.bigText} completed={step.filled} style={{ padding: "0 2px" }}>
                    <StepLabel
                        slotProps={{
                            label: { style: { marginTop: 2 } },
                        }}
                    >
                        <Typography style={{ fontSize: 7, fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>
                            {step.bigText}
                        </Typography>
                        {step.timestamp_utc && (
                            <Typography style={{ fontSize: 6.5, color: "#aaa", display: "block", lineHeight: 1.3 }}>
                                {step.smallText}
                                {moment(step.timestamp_utc).format("MM/DD HH:mm")}
                            </Typography>
                        )}
                    </StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

class OrderReceiptPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            account: localStorage.getItem("accountNumber"),
            activeStep: 0,
            showDeliveryDetails: true,
            steps: [],
            order: {},
        };
    }

    componentDidMount = () => {
        console.log("Order detail componentDidMount");
        this.initOrderDetail();
        setTimeout(() => {
            this.props.doPrintReceipt();
        }, 1000);
    };

    initOrderDetail = () => {
        this.setState({ order: this.props.order });
        httpRequest.getOrderTrack(this.props.order.jobID).then((response) => {
            console.log("getOrderTrack--" + JSON.stringify(response.data));
            var tempTrackList = response.data.map((option, index) => {
                return {
                    ...option,
                    id: index,
                };
            });
            this.setState({ steps: tempTrackList });
        });
    };

    formatOrderTime(utcTimestamp) {
        const d = new Date(utcTimestamp);
        const ianaZone = tzMap[this.state.order?.timezone];
        if (!ianaZone) return moment.utc(utcTimestamp).format("MM-DD") + " @ " + moment.utc(utcTimestamp).format("LT");
        const opts = { timeZone: ianaZone };
        const dateStr = d.toLocaleDateString("en-US", { ...opts, month: "2-digit", day: "2-digit" });
        const timeStr = d.toLocaleTimeString("en-US", { ...opts, hour: "numeric", minute: "2-digit" });
        const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (ianaZone !== localZone) {
            const tzAbbr = d
                .toLocaleTimeString("en-US", { ...opts, timeZoneName: "short" })
                .split(" ")
                .pop();
            return dateStr + " @ " + timeStr + " " + tzAbbr;
        }
        return dateStr + " @ " + timeStr;
    }

    renderStepContentText(label) {
        const str = " Click Here to Track >>> ";
        return (
            <div>
                <Typography>{label.bigText}</Typography>
                <Typography variant="caption">{label.smallText + this.formatOrderTime(label.timestamp_utc)}</Typography>
                {label.img_url && (
                    <span>
                        <a> {label.img_text} </a>
                        <div>
                            <img style={{ width: "80px", height: "60px" }} src={label.img_url} />
                        </div>
                    </span>
                )}
            </div>
        );
    }

    renderFromAddress() {
        const o = this.state.order;
        const suite = o.sSuite ? `${o.sSuite}, ` : "";
        return (
            <>
                {`${suite}${o.sAddress} ${o.sQuad}`.trim()}
                <br />
                {`${o.sCity}, ${o.sProv}, ${o.sPostal}, ${o.sCountry}`}
            </>
        );
    }

    renderToAddress() {
        const o = this.state.order;
        const suite = o.cSuite ? `${o.cSuite}, ` : "";
        return (
            <>
                {`${suite}${o.cAddress} ${o.cQuad}`.trim()}
                <br />
                {`${o.cCity}, ${o.cProv}, ${o.cPostal}, ${o.cCountry}`}
            </>
        );
    }

    renderDeliveryDetailslField = (label, value, size) => {
        return (
            <Grid item xs={12} sm={size}>
                <Typography style={{ fontWeight: 600, fontSize: 14 }}>{label}</Typography>
                <Typography style={{ fontWeight: 200, fontSize: 13 }}>{value}</Typography>
            </Grid>
        );
    };

    renderDeliveryDetails() {
        return (
            <Grid container spacing={1}>
                {this.renderDeliveryDetailslField("LOCATION", getLocationString(this.state.order.location), 4)}
                {this.renderDeliveryDetailslField("BILLING REFERENCE #", this.state.order.CUST_REF, 4)}
                {this.renderDeliveryDetailslField("YOUR NAME & PHONE", this.state.order.CALLER, 4)}
                {this.renderDeliveryDetailslField(
                    "CALL IN",
                    `${this.state.order.dtCallIn_display} ${this.state.order.timezone}`,
                    6
                )}
                {this.renderDeliveryDetailslField(
                    "DUE BY",
                    `${this.state.order.dtDCT_display} ${this.state.order.timezone}`,
                    6
                )}
                {this.renderDeliveryDetailslField("DELIVERED", getStatusString(this.state.order.Status), 4)}
                {this.renderDeliveryDetailslField("SERVICE MINUTES", this.state.order.SER_TIME, 4)}
                {this.renderDeliveryDetailslField("RATE(BEFORE TAX)", ` $${this.state.order.RATE}`, 4)}
                {this.renderDeliveryDetailslField("PROOF OF DELIVERY", this.state.order.PROOF_DEL, 4)}
                {this.renderDeliveryDetailslField("GST", this.state.order.GST_RATE, 6)}
            </Grid>
        );
    }

    renderOrderDetailslField = (label, value, size1, size2) => {
        return (
            <Grid item xs={size1} md={size2}>
                <Typography style={{ fontWeight: 600, fontSize: 14 }}>{label}</Typography>
                <Typography style={{ fontWeight: 200, fontSize: 13 }}>{value}</Typography>
            </Grid>
        );
    };

    render() {
        const order = this.state.order;
        const isDelivered = order?.Status >= 8;
        const statusColor = isDelivered ? "#2e7d32" : "#1565c0";

        return (
            <>
                <style>{`
                @media print {
                    @page { size: auto; margin: 12mm; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .receipt-section { break-inside: avoid; page-break-inside: avoid; }
                    .receipt-root { max-width: 100% !important; padding: 0 !important; }
                }
            `}</style>
                <div
                    className="receipt-root"
                    style={{
                        fontFamily: "'Arial', 'Helvetica', sans-serif",
                        color: "#1a1a1a",
                        padding: "28px 32px",
                        maxWidth: "740px",
                        margin: "0 auto",
                        background: "#fff",
                    }}
                >
                    {/* ── HEADER: LOGO + RECEIPT TITLE + TIMELINE ── */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 20,
                            marginBottom: 14,
                            paddingBottom: 14,
                            borderBottom: "1.5px solid #ddd",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                flexShrink: 0,
                                width: 140,
                            }}
                        >
                            <img
                                src={this.props.logoUrl || logo}
                                alt="Logo"
                                style={{
                                    height: "auto",
                                    width: "100%",
                                    display: "block",
                                    objectFit: "contain",
                                    objectPosition: "left",
                                    marginBottom: 8,
                                }}
                            />
                            <div>
                                <div
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                        color: "#1a1a1a",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Delivery Receipt
                                </div>
                                <div style={{ fontSize: 9, fontWeight: 500, color: "#999", marginTop: 2 }}>
                                    Order #{order.jobID}
                                </div>
                            </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0, paddingTop: 2, overflow: "hidden" }}>
                            <ReceiptTimeline steps={this.state.steps} />
                        </div>
                    </div>

                    {/* ── STATUS / TRACKING / SERVICE ── */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(12, 1fr)",
                            marginBottom: 20,
                            background: "#f9f9f9",
                            border: "1px solid #e8e8e8",
                            borderRadius: 4,
                            overflow: "hidden",
                        }}
                    >
                        <StatusCell
                            label="Status"
                            value={getStatusString(order.Status)}
                            color={statusColor}
                            colSpan={3}
                        />
                        <StatusCell label="Tracking Number" value={order.TrackNumber} mono colSpan={5} />
                        <StatusCell label="Service" value={order.serviceText} colSpan={4} />
                    </div>

                    {/* ── FROM / TO ── */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 24px 1fr",
                            marginBottom: 20,
                            alignItems: "stretch",
                        }}
                    >
                        <div
                            style={{
                                padding: "14px 16px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px 0 0 4px",
                                background: "#fafafa",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    color: "#888",
                                    marginBottom: 6,
                                }}
                            >
                                From
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
                                {order.sCompanyName || "—"}
                            </div>
                            <div style={{ fontSize: 11, color: "#555", lineHeight: 1.5 }}>
                                {this.renderFromAddress()}
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#e8f0fe",
                                borderTop: "1px solid #e0e0e0",
                                borderBottom: "1px solid #e0e0e0",
                            }}
                        >
                            <span style={{ fontSize: 14, color: "#1565c0", fontWeight: 700 }}>›</span>
                        </div>
                        <div
                            style={{
                                padding: "14px 16px",
                                border: "1px solid #1565c0",
                                borderRadius: "0 4px 4px 0",
                                background: "#f0f4ff",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    color: "#1565c0",
                                    marginBottom: 6,
                                }}
                            >
                                To
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
                                {order.cCompanyName || "—"}
                            </div>
                            <div style={{ fontSize: 11, color: "#444", lineHeight: 1.5 }}>{this.renderToAddress()}</div>
                        </div>
                    </div>

                    {/* ── SHIPMENT DETAILS ── */}
                    <div className="receipt-section" style={{ marginBottom: 20 }}>
                        <SectionHeading>Shipment Details</SectionHeading>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 16px" }}>
                            <Field label="Pieces" value={order.PIECES} />
                            <Field label="Weight" value={order.WEIGHT ? `${order.WEIGHT} lbs` : null} />
                            <Field label="Dimensions" value={order.DimsString || order.dimsString} />
                            <Field label="Description" value={order.DESC} />
                        </div>
                    </div>

                    {/* ── SPECIAL INSTRUCTIONS ── */}
                    {/* {order.DEL_INST && (
                        <div
                            style={{
                                marginBottom: 20,
                                padding: "10px 14px",
                                background: "#fffbf0",
                                borderLeft: "3px solid #f5a623",
                                borderRadius: "0 4px 4px 0",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    color: "#c47a00",
                                    marginBottom: 4,
                                }}
                            >
                                Instructions
                            </div>
                            <div style={{ fontSize: 12, color: "#1a1a1a", lineHeight: 1.5 }}>{order.DEL_INST}</div>
                        </div>
                    )} */}

                    <div style={{ marginBottom: 20 }}>
                        <TagsPanel title={<SectionHeading noBorder>Tags</SectionHeading>} tagIcons={order.tagIcons} />
                    </div>

                    {/* ── DELIVERY DETAILS ── */}
                    <div className="receipt-section" style={{ marginBottom: 20 }}>
                        <SectionHeading>Delivery Details</SectionHeading>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0 16px" }}>
                            <Field label="Signed By" value={order.PROOF_DEL} />
                            <Field
                                label="Delivered"
                                value={
                                    order.dtDelivered_display
                                        ? `${order.dtDelivered_display} ${order.timezone || ""}`.trim()
                                        : null
                                }
                            />
                            <Field label="Delivered To" value={getLocationString(order.location)} />
                            <Field label="Caller" value={order.CALLER} />
                            <Field
                                label="Call In"
                                value={
                                    order.dtCallIn_display
                                        ? `${order.dtCallIn_display} ${order.timezone || ""}`.trim()
                                        : null
                                }
                            />
                            <Field
                                label="Due By"
                                value={
                                    order.dtDCT_display ? `${order.dtDCT_display} ${order.timezone || ""}`.trim() : null
                                }
                            />
                            <Field label="Billing Reference" value={order.CUST_REF} />
                            <Field label="Accessories" value={order.Accessories} />
                        </div>
                    </div>

                    {/* ── PROOF OF DELIVERY ── */}
                    {(order.signatureURL || isDelivered) && (
                        <div className="receipt-section" style={{ marginBottom: 20 }}>
                            <SectionHeading>Proof of Delivery</SectionHeading>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
                                <div
                                    style={{
                                        flex: "0 0 auto",
                                        border: "1px solid #ddd",
                                        borderRadius: 4,
                                        padding: "10px 14px",
                                        background: "#fafafa",
                                        minWidth: 200,
                                        textAlign: "center",
                                    }}
                                >
                                    {order.signatureURL ? (
                                        <>
                                            <img
                                                src={order.signatureURL}
                                                alt="Customer Signature"
                                                style={{
                                                    display: "block",
                                                    maxWidth: "100%",
                                                    maxHeight: 60,
                                                    width: "auto",
                                                    height: "auto",
                                                    margin: "0 auto",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <div
                                                style={{
                                                    fontSize: 9,
                                                    color: "#aaa",
                                                    marginTop: 6,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                }}
                                            >
                                                Signature on file
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{ fontSize: 11, color: "#aaa", padding: "12px 0" }}>
                                            No signature captured
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", flex: 1 }}
                                >
                                    <Field label="Signed By" value={order.PROOF_DEL} />
                                    <Field
                                        label="Delivered"
                                        value={
                                            order.dtDelivered_display
                                                ? `${order.dtDelivered_display} ${order.timezone || ""}`.trim()
                                                : null
                                        }
                                    />
                                    <Field label="Delivered To" value={getLocationString(order.location)} />
                                    <Field label="Status" value={getStatusString(order.Status)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── BILLING ── */}
                    {this.props.bills && this.props.bills.length > 0 && (
                        <div className="receipt-section" style={{ marginBottom: 20 }}>
                            <SectionHeading noBorder>Bills</SectionHeading>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: "4px 6px",
                                                fontWeight: 700,
                                                color: "#555",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                fontSize: 9,
                                            }}
                                        >
                                            Title
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                padding: "4px 6px",
                                                fontWeight: 700,
                                                color: "#555",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                fontSize: 9,
                                            }}
                                        >
                                            Details
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "right",
                                                padding: "4px 6px",
                                                fontWeight: 700,
                                                color: "#555",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                                fontSize: 9,
                                            }}
                                        >
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.props.bills.map((bill, i) => {
                                        const isTotal = bill.title === "Trip Total Rate (pre Tax)";
                                        return (
                                            <tr
                                                key={i}
                                                style={{
                                                    borderBottom: "1px solid #f0f0f0",
                                                    borderTop: isTotal ? "1px solid #f0f0f0" : "none",
                                                }}
                                            >
                                                <td style={{ padding: "5px 6px", fontWeight: isTotal ? 700 : 400 }}>
                                                    {bill.title}
                                                </td>
                                                <td
                                                    style={{
                                                        padding: "5px 6px",
                                                        color: "#666",
                                                        fontWeight: isTotal ? 700 : 400,
                                                    }}
                                                >
                                                    {bill.details}
                                                </td>
                                                <td
                                                    style={{
                                                        padding: "5px 6px",
                                                        textAlign: "right",
                                                        fontFamily: "monospace",
                                                        fontWeight: isTotal ? 700 : 400,
                                                    }}
                                                >
                                                    ${parseFloat(bill.dollarValue).toFixed(2)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── FOOTER ── */}
                    <div
                        style={{
                            marginTop: 28,
                            paddingTop: 10,
                            borderTop: "1px solid #e0e0e0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <div style={{ fontSize: 9, color: "#aaa", letterSpacing: "0.3px" }}>
                            Automatically generated receipt — Order #{order.jobID}
                        </div>
                        <div style={{ fontSize: 9, color: "#aaa" }}>
                            {order.dtDelivered_display
                                ? `Delivered: ${order.dtDelivered_display}`
                                : order.dtDCT_display
                                  ? `Due: ${order.dtDCT_display}`
                                  : ""}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default OrderReceiptPanel;
